import type { TrackData } from '../track/types';

/** North-up mini-map of the day's circuit with the car and the ghost. */
export class MiniMap {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private base: HTMLCanvasElement | null = null;
  private scale = 1;
  private ox = 0;
  private oy = 0;
  private size = 0;
  /**
   * Its size on screen, as the browser last reported it. Measuring it every frame (as it used to) made the
   * browser lay the whole page out right then, mid-frame: at the finish line, with the lap card and banners
   * just changed, that was a visible hitch.
   */
  private cssSize: number | null = null;

  constructor(parent: HTMLElement, private track: TrackData) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'minimap';
    parent.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver((entries) => {
        const r = entries[0].contentRect;
        this.cssSize = Math.min(r.width, r.height);
      }).observe(this.canvas);
    }
  }

  setTrack(t: TrackData): void {
    this.track = t;
    this.base = null;
  }

  private prepare(): void {
    let css = this.cssSize;
    if (css === null) {
      const r = this.canvas.getBoundingClientRect();
      css = Math.min(r.width, r.height);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.max(64, Math.round(css * dpr));
    if (this.base && size === this.size) return;
    this.size = size;
    this.canvas.width = size;
    this.canvas.height = size;
    const t = this.track;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let i = 0; i < t.n; i++) {
      minX = Math.min(minX, t.px[i]);
      maxX = Math.max(maxX, t.px[i]);
      minY = Math.min(minY, t.py[i]);
      maxY = Math.max(maxY, t.py[i]);
    }
    const pad = size * 0.1;
    this.scale = (size - pad * 2) / Math.max(maxX - minX, maxY - minY);
    this.ox = pad + ((size - pad * 2) - (maxX - minX) * this.scale) / 2 - minX * this.scale;
    this.oy = pad + ((size - pad * 2) - (maxY - minY) * this.scale) / 2 + maxY * this.scale;
    const b = document.createElement('canvas');
    b.width = size;
    b.height = size;
    const g = b.getContext('2d')!;
    const path = (): void => {
      g.beginPath();
      for (let i = 0; i <= t.n; i++) {
        const k = i % t.n;
        const [x, y] = this.map(t.px[k], t.py[k]);
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.closePath();
    };
    g.lineJoin = 'round';
    g.lineCap = 'round';
    g.strokeStyle = 'rgba(59, 42, 30, 0.55)';
    g.lineWidth = size * 0.07;
    path();
    g.stroke();
    g.strokeStyle = '#F3E6C8';
    g.lineWidth = size * 0.035;
    path();
    g.stroke();
    // A crossover: the bridge drawn again on top, outlined, so it reads as passing over.
    const c = t.crossing;
    if (c) {
      const span: number[] = [];
      for (let i = 0; i < t.n; i++) {
        let d = Math.abs(t.s[i] - c.sOver);
        if (d > t.length / 2) d = t.length - d;
        if (d <= 22) span.push(i);
      }
      span.sort((a, b) => ((a - c.over + t.n) % t.n > t.n / 2 ? a - t.n : a) - ((b - c.over + t.n) % t.n > t.n / 2 ? b - t.n : b));
      const bridge = (): void => {
        g.beginPath();
        span.forEach((k, q) => {
          const [x, y] = this.map(t.px[k], t.py[k]);
          if (q === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
        });
      };
      g.lineCap = 'butt';
      g.strokeStyle = 'rgba(59, 42, 30, 0.9)';
      g.lineWidth = size * 0.065;
      bridge();
      g.stroke();
      g.strokeStyle = '#F3E6C8';
      g.lineWidth = size * 0.035;
      bridge();
      g.stroke();
      g.lineCap = 'round';
    }
    // Start/finish tick.
    const [sx, sy] = this.map(t.px[0], t.py[0]);
    const nx = -t.ty[0];
    const ny = t.tx[0];
    g.strokeStyle = '#3B2A1E';
    g.lineWidth = size * 0.022;
    g.beginPath();
    g.moveTo(sx - nx * size * 0.05, sy + ny * size * 0.05);
    g.lineTo(sx + nx * size * 0.05, sy - ny * size * 0.05);
    g.stroke();
    this.base = b;
  }

  private map(x: number, y: number): [number, number] {
    return [this.ox + x * this.scale, this.oy - y * this.scale];
  }

  draw(car: { x: number; y: number }, ghost: { x: number; y: number } | null): void {
    this.prepare();
    const g = this.ctx;
    const s = this.size;
    g.clearRect(0, 0, s, s);
    if (this.base) g.drawImage(this.base, 0, 0);
    const dot = (x: number, y: number, fill: string, r: number): void => {
      const [px, py] = this.map(x, y);
      g.beginPath();
      g.arc(px, py, r, 0, Math.PI * 2);
      g.fillStyle = fill;
      g.fill();
      g.lineWidth = s * 0.012;
      g.strokeStyle = '#F3E6C8';
      g.stroke();
    };
    if (ghost) dot(ghost.x, ghost.y, '#2F4A2E', s * 0.03);
    dot(car.x, car.y, '#B8352B', s * 0.038);
  }
}
